// Illustrative member scenarios — FICTIONAL examples of how a member could use
// a track. Never present these as testimonials, reviews or customer results.
export const ILLUSTRATIVE_DISCLAIMER =
  "Illustrative example showing how a member could use this Stag Vault track. This is not a verified customer testimonial or guaranteed result.";

export const SCENARIO_FOOTNOTE =
  "This example demonstrates a possible workflow and is not a quote from a real customer. Individual outcomes depend on experience, effort, demand and execution.";

export const ILLUSTRATIVE_SCENARIOS = [
  {
    id: "jamie-joiner",
    name: "Jamie",
    role: "Independent Joiner",
    category: "Local trades",
    trackSlug: "trades-maintenance-plan-launch",
    trackName: "Trades Planned Maintenance Plan Launch Studio",
    before: "Jamie already understands joinery but does not know how to package that experience into a recurring service. Work arrives as one-off jobs and quiet months are unpredictable.",
    stages: ["Buyer selection — landlords and small commercial units", "Offer development — three service tiers", "Pricing — scope-based bands", "Asset creation — sales sheet and templates", "Outreach — landlord introduction sequence"],
    asset: "A planned-maintenance package for landlords and small commercial properties.",
    routeToMarket: "Direct email introductions to local letting agents, followed by a one-page sales sheet left after each completed job.",
    deliverables: ["Three-tier maintenance plan", "Landlord introduction email", "Property inspection checklist", "Quotation template", "Renewal reminder sequence", "One-page sales sheet"],
    suggestedOffer: "Suggested offer price: £149–£499 per year, depending on the property and scope.",
    nextStep: "Offer the entry tier to three existing customers as a pilot and refine the checklist from their feedback.",
    sampleIds: ["planned-maintenance-proposal", "maintenance-pricing-menu"],
  },
  {
    id: "priya-lettings",
    name: "Priya",
    role: "Small Portfolio Landlord",
    category: "Property",
    trackSlug: "uk-landlord-compliance-planner-kits",
    trackName: "UK Landlord Compliance Planner Kits",
    before: "Priya manages four rental properties and keeps certificates, renewal dates and inspection notes across email threads and a notebook. She suspects other small landlords have the same problem.",
    stages: ["Validation — which compliance tasks cause the most stress", "Offer development — a printable and digital planner kit", "Asset creation — planner pages and checklists", "Marketing — Etsy listing copy", "Quality assurance — legal wording review"],
    asset: "A landlord compliance planner kit sold as a digital download.",
    routeToMarket: "An Etsy listing plus a Pinterest board of planner previews; a short landing page for direct sales.",
    deliverables: ["12-month compliance calendar page", "Certificate tracker", "Inspection log template", "Tenant document checklist", "Listing copy and preview images", "Buyer onboarding note"],
    suggestedOffer: "Suggested listing price: £9–£19 for the digital kit.",
    nextStep: "Publish the listing, gather ten reviews from real buyers, then iterate the pages that get the most questions.",
    sampleIds: ["landlord-planner-page"],
  },
  {
    id: "sam-planner",
    name: "Sam",
    role: "Former Events Assistant",
    category: "Digital products",
    trackSlug: "ai-wedding-planning-planner-packs",
    trackName: "AI Wedding Planning Planner Packs",
    before: "Sam has hands-on wedding coordination experience but no product, audience or design background. The goal is a first digital product that can be sold repeatedly.",
    stages: ["Micro-niche discovery — budget-conscious couples", "Offer development — a structured planner pack", "Asset creation — editable planner sections", "Marketing — seven-day launch plan", "Review and improvement — post-launch survey"],
    asset: "A budget-honest wedding planner pack with editable timelines and supplier trackers.",
    routeToMarket: "Etsy and Pinterest first, with a seven-day launch sequence across a small Instagram following.",
    deliverables: ["Planner pack outline", "Budget tracker page", "Supplier comparison sheet", "12-month timeline", "Seven-day launch plan", "Listing description"],
    suggestedOffer: "Suggested listing price: £12–£24 per pack.",
    nextStep: "Launch a single pack, then test one seasonal variant based on buyer questions.",
    sampleIds: ["planner-pack-launch-plan"],
  },
  {
    id: "aisha-creator",
    name: "Aisha",
    role: "Part-Time Content Creator",
    category: "Content creation",
    trackSlug: "youtube-automation",
    trackName: "YouTube Automation",
    before: "Aisha posts inconsistently and spends most of her limited time deciding what to make. She wants a repeatable content system rather than another burst of motivation.",
    stages: ["Niche picker — one topic, one audience", "Format design — a repeatable episode structure", "Asset creation — 30-day content pack", "Marketing — hook and title testing", "Review — weekly retention check"],
    asset: "A 30-day content pack with scripts, hooks and a publishing calendar for one channel.",
    routeToMarket: "A single channel with a fixed weekly cadence, repurposed into short-form clips.",
    deliverables: ["Channel positioning statement", "10 episode outlines", "30 hook variations", "Publishing calendar", "Thumbnail brief template", "Weekly review checklist"],
    suggestedOffer: "No product price — the asset is the channel system itself; monetisation options are covered later in the track.",
    nextStep: "Publish the first four episodes on schedule before changing anything.",
    sampleIds: ["creator-content-pack"],
  },
  {
    id: "marcus-freelance",
    name: "Marcus",
    role: "Freelance Marketer",
    category: "Freelance services",
    trackSlug: "local-seo-audits",
    trackName: "AI Local SEO Audits",
    before: "Marcus sells ad-hoc marketing help to local businesses and finds each project starts from scratch. He wants a productised offer he can quote and deliver the same way every time.",
    stages: ["Buyer selection — local service businesses", "Offer development — fixed-scope audit plus retainer", "Asset creation — proposal and audit template", "Outreach — five-touch email sequence", "Delivery — competitor snapshot dashboard"],
    asset: "A fixed-scope local SEO audit with an optional monthly retainer.",
    routeToMarket: "Cold outreach to local businesses with visible ranking gaps, backed by a one-page proposal.",
    deliverables: ["One-page service proposal", "Audit checklist", "Competitor-intelligence dashboard", "Five-touch outreach sequence", "Retainer scope sheet", "Client onboarding form"],
    suggestedOffer: "Suggested offer price: £299 audit, optional £199/month retainer.",
    nextStep: "Deliver two audits at the entry price, then use the results to refine the proposal.",
    sampleIds: ["local-seo-outreach-sequence", "competitor-intelligence-dashboard"],
  },
  {
    id: "elena-cafe",
    name: "Elena",
    role: "Independent Café Owner",
    category: "Hospitality",
    trackSlug: "cafe-allergen-matrix-menu-compliance-service",
    trackName: "Café Allergen Matrix & Menu-Compliance Service",
    before: "Elena runs a small café and rebuilds her allergen information by hand every time the menu changes. She realises neighbouring cafés face the same repeated task.",
    stages: ["Validation — interviews with three café owners", "Offer development — a done-for-you menu compliance service", "Asset creation — allergen matrix template and onboarding form", "Delivery — a repeatable checklist", "Upselling — seasonal menu updates"],
    asset: "A menu-compliance service that produces an allergen matrix and customer-facing menu notes for independent cafés.",
    routeToMarket: "Introductions through local supplier reps and a printed one-page flyer for nearby venues.",
    deliverables: ["Client onboarding form", "Allergen matrix template", "Menu-notes wording sheet", "Delivery checklist", "Seasonal update reminder", "One-page flyer"],
    suggestedOffer: "Suggested offer price: £120–£250 per menu, with seasonal updates priced separately.",
    nextStep: "Run the service for her own café first, then offer it to two neighbouring venues.",
    sampleIds: ["cafe-onboarding-form", "cafe-delivery-checklist"],
  },
];

export const getScenario = (id) => ILLUSTRATIVE_SCENARIOS.find((s) => s.id === id) || null;
